import { views } from 'cng-web-lib'
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, Link } from 'react-router-dom'
import NaCommonTranslationText from 'src/common/NaCommonTranslationText'
import styles from './login.module.css'
import { Box, useMediaQuery, useTheme } from '@mui/material'

const { CngLoginView } = views
const UPDATE_PW_LINK_KEY = 'LINK-UPDATE-PASSWORD'

/**
 * HelperContext - Manages helper form state and methods
 */
const HelperContext = createContext()

/**
 * HelperProvider - Context Provider Component
 * Wraps components to provide helper state and functions
 */
export const HelperProvider = ({ children }) => {
  const [loginError, setLoginError] = useState(null)
  const location = useLocation()

  const theme = useTheme()
  const isBelowSm = useMediaQuery(theme.breakpoints.down('sm'))

  const cngRef = useRef(null)
  const naCommonTranslationText = NaCommonTranslationText()
  const isLoginPage = location.pathname === '/login'

  useEffect(() => {
    const root = cngRef.current
    if (!root) return

    const notifyIfChanged = () => {
      const msgElem = root.querySelector('.Mui-error')
      const text = msgElem?.textContent?.trim() || ''
      
      // Check if LINK-UPDATE-PASSWORD exists in the parent container
      const parentBox = msgElem?.parentElement
      const hasUpdatePasswordLink = parentBox?.textContent?.includes(UPDATE_PW_LINK_KEY)
      
      if (hasUpdatePasswordLink) {
        // Handle password expiration scenario
        setLoginError(<>
          {text}<br/>
          <Link to='/update-password'>{naCommonTranslationText.updatePassword}</Link>
        </>)
      } else {
        setLoginError(text)
      }
    }

    // Initial read
    notifyIfChanged()

    const observer = new MutationObserver(() => {
      notifyIfChanged()
    })

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <HelperContext.Provider value={{
      styles, cngRef,
      loginError, setLoginError,
      isBelowSm,
    }}>
      {children}
      {isLoginPage && (
        <Box width='100%' ref={cngRef} display='none'>
          <CngLoginView
            renderHeaderContent={() => { "" }}
            renderFooterContent={() => { "" }}
            renderLogo={() => { "" }}
            renderExpiredPasswordMessage={
              {
                errorMessageKey: "password has expired",
                newErrorMessage: naCommonTranslationText.userPasswordExpiredMsg,
                renderLinkContent: () => UPDATE_PW_LINK_KEY
              }}
          />
        </Box>
      )}
    </HelperContext.Provider>
  )
}

/**
 * Custom Hook - useHelper
 * Use this hook to access the HelperContext in any component
 */
export const useHelper = () => {
  const context = useContext(HelperContext)
  if (!context) {
    throw new Error('useHelper must be used within a HelperProvider')
  }
  return context
}

export default HelperContext
